
import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import axios from 'axios';
import LogoLoader from "../component/logo/LogoLoader";

/*
 * MemberMeld WhatsApp Business App Coexistence Signup
 *
 * Required:
 *   appId       - Meta App ID
 *   configId    - Embedded Signup / Login for Business config ID
 *   backendUrl  - Your HTTPS Firebase Function/API base URL
 *   getIdToken  - Function returning the signed-in Firebase user's ID token
 *
 * Optional:
 *   onConnected - Called after your backend confirms onboarding
 */

const FACEBOOK_SDK_URL =
    "https://connect.facebook.net/en_US/sdk.js";

const FACEBOOK_SDK_ID = "facebook-jssdk";

const ALLOWED_FACEBOOK_ORIGINS = new Set([
    "https://facebook.com",
    "https://www.facebook.com",
    "https://web.facebook.com",
]);

/**
 * Load and initialize the Facebook SDK once.
 * Handles an SDK that is already present or still loading.
 */
function loadFacebookSdk(appId) {
    console.log("A1");
    if (!appId) {
        return Promise.reject(
            new Error("Meta App ID is missing.")
        );
    }

    if (window.FB) {
        console.log("A3")
        const initializedAppId =
            window.__memberMeldFacebookAppId;

        if (initializedAppId && initializedAppId !== appId) {
            return Promise.reject(
                new Error(
                    "Facebook SDK is initialized with a different App ID."
                )
            );
        }

        if (!initializedAppId) {
            window.FB.init({
                appId,
                autoLogAppEvents: true,
                //cookie: true,
                xfbml: true,
                version: "v26.0",
            });

            window.__memberMeldFacebookAppId = appId;
        }

        return Promise.resolve(window.FB);
    }

    if (window.__memberMeldFacebookSdkPromise) {
        return window.__memberMeldFacebookSdkPromise.then(() => {
            if (
                window.__memberMeldFacebookAppId &&
                window.__memberMeldFacebookAppId !== appId
            ) {
                throw new Error(
                    "Facebook SDK is initialized with a different App ID."
                );
            }

            return window.FB;
        });
    }
    window.__memberMeldFacebookSdkPromise = new Promise(
        (resolve, reject) => {
            let settled = false;
            let pollId;
            let timeoutId;

            const finish = (error) => {
                if (settled) return;
                settled = true;

                window.clearInterval(pollId);
                window.clearTimeout(timeoutId);

                if (error) {
                    window.__memberMeldFacebookSdkPromise = null;
                    reject(error);
                    return;
                }

                resolve(window.FB);
            };

            const initialize = () => {
                if (!window.FB) return;

                try {
                    const initializedAppId =
                        window.__memberMeldFacebookAppId;

                    if (
                        initializedAppId &&
                        initializedAppId !== appId
                    ) {
                        finish(
                            new Error(
                                "Facebook SDK is initialized with a different App ID."
                            )
                        );
                        return;
                    }

                    if (!initializedAppId) {
                        console.log("A61");
                        window.FB.init({
                            appId,
                            autoLogAppEvents: true,
                            //cookie: true,
                            xfbml: true,
                            version: "v26.0",
                        });

                        window.__memberMeldFacebookAppId = appId;
                    }

                    finish();
                } catch (error) {
                    finish(error);
                }
            };

            // The SDK calls this when its script becomes available.
            const previousInit = window.fbAsyncInit;

            window.fbAsyncInit = function () {
                if (typeof previousInit === "function") {
                    previousInit();
                }

                initialize();
            };

            let script = document.getElementById(
                FACEBOOK_SDK_ID
            );

            if (!script) {
                script = document.createElement("script");
                script.id = FACEBOOK_SDK_ID;
                script.src = FACEBOOK_SDK_URL;
                script.async = true;
                script.defer = true;
                script.crossOrigin = "anonymous";

                script.onerror = () => {
                    finish(
                        new Error(
                            "Unable to load the Facebook SDK. Please try again."
                        )
                    );
                };

                document.head.appendChild(script);
            }

            // Also handles a script element created elsewhere.
            script.addEventListener("load", initialize);

            // Polling covers the case where the script already existed
            // and its load event fired before this component mounted.
            pollId = window.setInterval(initialize, 100);

            timeoutId = window.setTimeout(() => {
                finish(
                    new Error(
                        "Facebook SDK initialization timed out. Check your connection and try again."
                    )
                );
            }, 20000);

            initialize();
        }
    );

    return window.__memberMeldFacebookSdkPromise;
}

// Keep your existing project imports for these:
// import LogoLoader from "...";
// import { loadFacebookSdk, ALLOWED_FACEBOOK_ORIGINS } from "...";
 
/* ------------------------------------------------------------------ */
/* Styles (kept in a <style> tag so media queries / focus / dark mode  */
/* work; inline styles cannot do these)                                */
/* ------------------------------------------------------------------ */
const css = `
.mm-wa {
  --mm-bg: #f4f6f8;
  --mm-card: #ffffff;
  --mm-text: #111827;
  --mm-muted: #6b7280;
  --mm-border: #e5e7eb;
  --mm-primary: #1877f2;
  --mm-primary-dark: #145fc4;
  --mm-green: #128c7e;
  --mm-green-bg: #e8f6f3;
  --mm-error-bg: #fef2f2;
  --mm-error-text: #991b1b;
  --mm-error-border: #fecaca;
  min-height: 100vh;
  min-height: 10dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
  background: var(--mm-bg);
  color: var(--mm-text);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
  -webkit-text-size-adjust: 100%;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  width: 100%;
}
.mm-wa *, .mm-wa *::before, .mm-wa *::after { box-sizing: border-box; }
.mm-wa-card {
  width: 100%;
  max-width: 460px;
  background: var(--mm-card);
  border: 1px solid var(--mm-border);
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(17, 24, 39, 0.06);
  overflow: hidden;
}
.mm-wa-secure-bar {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  padding: 10px 16px;
  background: var(--mm-green-bg);
  color: var(--mm-green);
  font-size: 13px; font-weight: 600;
  border-bottom: 1px solid var(--mm-border);
}
.mm-wa-body { padding: 28px 24px 24px; }
.mm-wa-brand { display: flex; align-items: center; justify-content: center; gap: 12px; margin-bottom: 20px; }
.mm-wa-logo {
  width: 52px; height: 52px; border-radius: 14px;
  background: #111827; color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-weight: 800; font-size: 20px; letter-spacing: -0.5px;
}
.mm-wa-link-dots { color: var(--mm-muted); letter-spacing: 3px; font-size: 18px; }
.mm-wa-wa {
  width: 52px; height: 52px; border-radius: 14px;
  background: #25d366; display: flex; align-items: center; justify-content: center;
}
.mm-wa-mm {
  width: 52px; height: 52px; border-radius: 14px;
  background: #003566; display: flex; align-items: center; justify-content: center;
}
.mm-wa h1 { font-size: 22px; line-height: 1.3; margin: 0 0 8px; text-align: center; font-weight: 700; }
.mm-wa-sub { margin: 0 0 20px; text-align: center; color: var(--mm-muted); font-size: 15px; line-height: 1.55; }
.mm-wa-user {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 14px; margin-bottom: 20px;
  border: 1px solid var(--mm-border); border-radius: 12px; background: #fafafa;
}
.mm-wa-avatar {
  flex: 0 0 auto; width: 40px; height: 40px; border-radius: 50%;
  background: #dbeafe; color: #1d4ed8; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
}
.mm-wa-user-text { min-width: 0; }
.mm-wa-user-name { font-weight: 600; font-size: 15px; }
.mm-wa-user-email { font-size: 13px; color: var(--mm-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mm-wa-section-title { font-size: 12px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: var(--mm-muted); margin: 0 0 10px; }
.mm-wa-list { list-style: none; margin: 0 0 20px; padding: 0; display: grid; gap: 10px; }
.mm-wa-list li { display: flex; gap: 10px; align-items: flex-start; font-size: 14px; line-height: 1.5; color: #374151; }
.mm-wa-list svg { flex: 0 0 auto; margin-top: 2px; }
.mm-wa-btn {
  width: 100%; min-height: 50px; padding: 12px 20px;
  border: 0; border-radius: 10px;
  background: var(--mm-primary); color: #fff;
  font-size: 16px; font-weight: 600; font-family: inherit;
  display: flex; align-items: center; justify-content: center; gap: 10px;
  cursor: pointer; transition: background .15s ease, transform .05s ease;
  -webkit-tap-highlight-color: transparent;
}
.mm-wa-btn:hover:not(:disabled) { background: var(--mm-primary-dark); }
.mm-wa-btn:active:not(:disabled) { transform: scale(0.99); }
.mm-wa-btn:focus-visible { outline: 3px solid #93c5fd; outline-offset: 2px; }
.mm-wa-btn:disabled { background: #9ca3af; cursor: not-allowed; }
.mm-wa-spinner {
  width: 18px; height: 18px; border-radius: 50%;
  border: 2px solid rgba(255,255,255,.5); border-top-color: #fff;
  animation: mm-spin .8s linear infinite;
}
@keyframes mm-spin { to { transform: rotate(360deg); } }
.mm-wa-status { margin: 14px 0 0; text-align: center; font-size: 14px; color: #374151; min-height: 20px; }
.mm-wa-error {
  margin-top: 12px; padding: 12px 14px; border-radius: 10px;
  background: var(--mm-error-bg); color: var(--mm-error-text);
  border: 1px solid var(--mm-error-border); font-size: 14px; line-height: 1.5;
}
.mm-wa-note {
  margin: 20px 0 0; padding-top: 16px; border-top: 1px solid var(--mm-border);
  font-size: 12.5px; line-height: 1.55; color: var(--mm-muted); text-align: center;
}
.mm-wa-note a { color: var(--mm-primary); text-decoration: none; }
.mm-wa-note a:hover { text-decoration: underline; }
.mm-wa-success { text-align: center; padding: 12px 0 4px; }
.mm-wa-success-icon {
  width: 64px; height: 64px; border-radius: 50%; margin: 0 auto 16px;
  background: #dcfce7; display: flex; align-items: center; justify-content: center;
}
@media (min-width: 640px) {
  .mm-wa-body { padding: 36px 36px 28px; }
  .mm-wa h1 { font-size: 24px; }
}
@media (max-width: 380px) {
  .mm-wa { padding: 10px; }
  .mm-wa-body { padding: 22px 16px 18px; }
}
@media (prefers-reduced-motion: reduce) {
  .mm-wa-spinner { animation-duration: 2s; }
  .mm-wa-btn { transition: none; }
}
`;
 
/* ------------------------------------------------------------------ */
/* Small inline icons                                                  */
/* ------------------------------------------------------------------ */
const LockIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);
const CheckIcon = ({ color = "#16a34a", size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
       strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);
const CrossIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6b7280"
       strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);
const WhatsAppGlyph = () => (
  <svg width="30" height="30" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
    <path d="M12.04 2a9.9 9.9 0 0 0-8.5 14.9L2 22l5.26-1.38A9.94 9.94 0 1 0 12.04 2Zm0 18.1a8.1 8.1 0 0 1-4.13-1.13l-.3-.18-3.12.82.83-3.04-.2-.31a8.1 8.1 0 1 1 6.92 3.84Zm4.45-6.06c-.24-.12-1.44-.71-1.66-.79-.22-.08-.39-.12-.55.12-.16.24-.63.79-.77.95-.14.16-.28.18-.52.06a6.6 6.6 0 0 1-1.95-1.2 7.3 7.3 0 0 1-1.35-1.68c-.14-.24-.02-.37.1-.49.11-.11.24-.28.36-.42.12-.14.16-.24.24-.4.08-.16.04-.3-.02-.42-.06-.12-.55-1.32-.75-1.8-.2-.47-.4-.4-.55-.41h-.47c-.16 0-.42.06-.64.3-.22.24-.84.82-.84 2s.86 2.32.98 2.48c.12.16 1.7 2.6 4.12 3.64.58.25 1.03.4 1.38.51.58.18 1.1.16 1.52.1.46-.07 1.44-.59 1.64-1.16.2-.57.2-1.06.14-1.16-.06-.1-.22-.16-.46-.28Z" />
  </svg>
);

const MemberMeldGlyph = () => (
 <img
    src="/logo192.png"
    width="40"
    height="40"
    alt="MemberMeld"
    aria-hidden="true"
    style={{ objectFit: "contain" }}
  />
);
 
/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */
const WhatsAppSignup = ({
  appId,
  configId,
  backendUrl,
  onConnected,
  clubId,
  privacyUrl = "/privacy",
  termsUrl = "/tos",
  supportEmail = "support@membermeld.com",
}) => {
  const [sdkReady, setSdkReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const [status, setStatus] = useState("Preparing secure connection…");
  const [error, setError] = useState("");
  const [member, setMember] = useState(null);
 
  const authorizationCodeRef = useRef(null);
  const sessionInfoRef = useRef(null);
  const completionStartedRef = useRef(false);
  const mountedRef = useRef(false);
 
  /** Both the OAuth callback and the postMessage event are needed (any order). */
  const completeSignup = useCallback(async () => {
    const code = authorizationCodeRef.current;
    const sessionInfo = sessionInfoRef.current;
 
    if (!code || !sessionInfo || completionStartedRef.current) return;
    if (sessionInfo.event !== "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING") return;
 
    if (!sessionInfo.data?.waba_id) {
      setError("Meta did not return the WhatsApp Business Account ID.");
      setLoading(false);
      return;
    }
 
    completionStartedRef.current = true;
    setError("");
    setStatus("Finalizing your WhatsApp connection…");
 
    try {
      // Backend must exchange the code. Never expose the Meta App Secret in React.
      const response = await fetch(`/api/completeWhatsAppSignup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code,
          wabaId: sessionInfo.data.waba_id,
          phoneNumberId: sessionInfo.data.phone_number_id ?? null,
          signupEvent: sessionInfo.event,
          sessionInfo,
          clubId,
        }),
      });
 
      const result = await response.json().catch(() => ({}));
 
      if (!response.ok) {
        throw new Error(
          result.message || result.error ||
            "MemberMeld could not finish the WhatsApp connection."
        );
      }
 
      if (mountedRef.current) {
        setStatus("");
        setLoading(false);
        setConnected(true);
        if (typeof onConnected === "function") onConnected(result);
      }
    } catch (err) {
      completionStartedRef.current = false;
      if (mountedRef.current) {
        setError(err.message || "Unable to finish WhatsApp onboarding.");
        setStatus("Connection could not be completed.");
        setLoading(false);
      }
    }
  }, [clubId, onConnected]);
 
  /** Load member info only when clubId changes (not on every re-render). */
  useEffect(() => {
    if (!clubId) return;
    let cancelled = false;
 
    (async () => {
      try {
        const res = await axios.get(`/api/getMemberInfo?id=${clubId}`);
        if (cancelled) return;
        const plans = [...(res.data.plans || [])].sort((a, b) => a.duration - b.duration);
        setMember({
          name: res.data.name,
          email: res.data.email,
          planEndDate: res.data.planEndDate,
          plans,
        });
      } catch {
        if (!cancelled) setError("We couldn't find your account. Please check your link.");
      }
    })();
 
    return () => { cancelled = true; };
  }, [clubId]);
 
  /** Meta SDK + Embedded Signup message listener. */
  useEffect(() => {
    mountedRef.current = true;
 
    const handleMessage = (event) => {
      if (!ALLOWED_FACEBOOK_ORIGINS.has(event.origin)) return;
 
      let message = event.data;
      if (typeof message === "string") {
        try { message = JSON.parse(message); } catch { return; }
      }
      if (!message || message.type !== "WA_EMBEDDED_SIGNUP") return;
 
      if (message.event === "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING") {
        sessionInfoRef.current = message;
        setStatus("WhatsApp setup completed. Finalizing connection…");
        void completeSignup();
        return;
      }
      if (message.event === "CANCEL") {
        setLoading(false);
        setStatus("");
        setError("You cancelled the connection. You can try again when you're ready.");
      }
      if (message.event === "ERROR") {
        setLoading(false);
        setStatus("");
        setError("Meta could not complete onboarding. Please try again.");
      }
    };
 
    window.addEventListener("message", handleMessage);
 
    loadFacebookSdk(appId)
      .then(() => {
        if (!mountedRef.current) return;
        setSdkReady(true);
        setStatus("Ready to connect.");
        setError("");
      })
      .catch((err) => {
        if (!mountedRef.current) return;
        setError(err.message);
        setStatus("Meta connection is unavailable right now.");
      });
 
    return () => {
      mountedRef.current = false;
      window.removeEventListener("message", handleMessage);
    };
  }, [appId, completeSignup]);
 
  /** Must run directly from the click — do not await anything before FB.login(). */
  const launchWhatsAppSignup = () => {
    setError("");
 
    if (!window.FB || !sdkReady) {
      setError("Meta is still loading. Please wait a moment and try again.");
      return;
    }
    if (!appId || !configId || !backendUrl) {
      setError("Missing Meta App ID, Signup Configuration ID, or backend URL.");
      return;
    }
 
    authorizationCodeRef.current = null;
    sessionInfoRef.current = null;
    completionStartedRef.current = false;
 
    setLoading(true);
    setStatus("Complete the steps in the Meta window.");
 
    try {
      window.FB.login(
        (response) => {
          if (response?.authResponse?.code) {
            authorizationCodeRef.current = response.authResponse.code;
            void completeSignup(); // never log or render the code
            return;
          }
          setLoading(false);
          setStatus("");
          setError(
            "Signup was not completed. It may have been cancelled, or your browser blocked the popup."
          );
        },
        {
          config_id: configId,
          response_type: "code",
          override_default_response_type: true,
          extras: {
            setup: {},
            featureType: "whatsapp_business_app_onboarding",
            version: "v4",
            sessionInfoVersion: "3",
          },
        }
      );
    } catch {
      setLoading(false);
      setStatus("");
      setError("Could not open Meta signup. Please allow popups for this site and try again.");
    }
  };
 
  if (!member && !error) return (
    <>
    <style>{css}</style>
    
    <div className="loading-container">
      <LogoLoader theme="light" />
    </div>
    </>
  );
 
  const initial = (member?.name || "?").trim().charAt(0).toUpperCase();
  const buttonDisabled = !sdkReady || loading || !member;
 
  return (
    <div className="mm-wa">
      <style>{css}</style>
 
      <main className="mm-wa-card" aria-labelledby="mm-wa-title">
        <div className="mm-wa-secure-bar">
          <LockIcon />
          <span>Secure connection</span>
        </div>
 
        <div className="mm-wa-body">
          {connected ? (
            <div className="mm-wa-success" role="status" aria-live="polite">
              <div className="mm-wa-success-icon">
                <CheckIcon size={32} />
              </div>
              <h1 id="mm-wa-title">WhatsApp connected</h1>
              <p className="mm-wa-sub">
                Your WhatsApp Business number is now linked to MemberMeld.
                You can close this page.
              </p>
            </div>
          ) : (
            <>
              <div className="mm-wa-brand" aria-hidden="true">
                <div className="mm-wa-mm"><MemberMeldGlyph /></div>
                <span className="mm-wa-link-dots">•••</span>
                <div className="mm-wa-wa"><WhatsAppGlyph /></div>
              </div>
 
              <h1 id="mm-wa-title">Connect WhatsApp Business</h1>
              <p className="mm-wa-sub">
                Link your WhatsApp Business number to MemberMeld to message
                your members. You'll finish the process in an official Meta window.
              </p>
 
              {member && (
                <div className="mm-wa-user">
                  <div className="mm-wa-avatar" aria-hidden="true">{initial}</div>
                  <div className="mm-wa-user-text">
                    <div className="mm-wa-user-name">{member.name}</div>
                    <div className="mm-wa-user-email">{member.email}</div>
                  </div>
                </div>
              )}
 
              <p className="mm-wa-section-title">What to expect</p>
              <ul className="mm-wa-list">
                <li><CheckIcon />A Meta (Facebook) window opens — sign in there, not on our site.</li>
                <li><CheckIcon />You may be asked to verify your number in your WhatsApp Business app.</li>
                <li><CheckIcon />You can keep using the WhatsApp Business app on your phone.</li>
                <li><CrossIcon />We never see or store your Facebook password.</li>
              </ul>
 
              <button
                type="button"
                className="mm-wa-btn"
                onClick={launchWhatsAppSignup}
                disabled={buttonDisabled}
                aria-busy={loading}
              >
                {loading ? (
                  <>
                    <span className="mm-wa-spinner" aria-hidden="true" />
                    Connecting…
                  </>
                ) : sdkReady ? (
                  <>
                    <LockIcon />
                    Continue with Meta
                  </>
                ) : (
                  "Loading secure connection…"
                )}
              </button>
 
              <p className="mm-wa-status" role="status" aria-live="polite">{status}</p>
 
              {error && (
                <div className="mm-wa-error" role="alert">{error}</div>
              )}
            </>
          )}
 
          <p className="mm-wa-note">
            Keep your phone nearby during setup. Make sure the address bar shows
            this site's name and a padlock before continuing.
            <br />
            <a href={privacyUrl} target="_blank" rel="noopener noreferrer">Privacy Policy</a>
            {" · "}
            <a href={termsUrl} target="_blank" rel="noopener noreferrer">Terms</a>
            {" · "}
            <a href={`mailto:${supportEmail}`}>Need help?</a>
          </p>
        </div>
      </main>
    </div>
  );
};
 
export default WhatsAppSignup;
 